import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export type ToastTone = 'ok' | 'bad';

interface Toast {
  id: number;
  text: string;
  tone: ToastTone;
}

interface ToastState {
  notify: (text: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastState | null>(null);

let nextId = 1;

/**
 * Transient confirmations, using the prototype's `toasts` / `toast` classes.
 *
 * The addition over the prototype is `aria-live`: it announced nothing, so a
 * screen-reader user got no feedback that their action had succeeded or failed
 * — the message simply appeared and vanished.
 *
 * `polite` for confirmations so they queue behind whatever is being read;
 * `assertive` for failures, which interrupt. A failure that waits its turn can
 * be missed entirely before it disappears.
 */
export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const notify = useCallback((text: string, tone: ToastTone = 'ok') => {
    const id = nextId++;
    setToasts((current) => [...current, { id, text, tone }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4000);
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div className="toasts">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="toast"
            role={toast.tone === 'bad' ? 'alert' : 'status'}
            aria-live={toast.tone === 'bad' ? 'assertive' : 'polite'}
          >
            {toast.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastState {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a ToastProvider.');
  return context;
}
